import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ShieldCheck, Lock, Unlock, Plus, Save, Mail, CreditCard, SlidersHorizontal, Receipt, Users as UsersIcon, AlertTriangle, Megaphone } from 'lucide-react';
import { fmtDate } from '../lib/share';
import { useLicense } from '../contexts/LicenseContext';
import VendorBilling, { ErrorLogs } from '../components/VendorBilling';
import TenantPlans from '../components/TenantPlans';
import SystemOps from '../components/SystemOps';
import RightHandManager from '../components/RightHandManager';
import MarketplaceAdmin from '../components/MarketplaceAdmin';

const ROLE_LABEL = { superadmin: 'Propietario (SuperAdmin)', admin: 'Admin sucursal', vendedor: 'Vendedor', despacho: 'Despacho', cliente: 'Cliente' };

const GmailCard = () => {
  const [f, setF] = useState({ username: '', password: '', from_name: 'SalesPRO' });
  const [configured, setConfigured] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.getGlobalSmtp().then((r) => { setConfigured(r.data.configured); if (r.data.username) setF((x) => ({ ...x, username: r.data.username, from_name: r.data.from_name || x.from_name })); }).catch(() => {}); }, []);
  const save = async () => {
    try { await api.saveGlobalSmtp(f); setConfigured(true); toast.success('SalesPRO: Gmail del sistema guardado'); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };
  const sendTest = async () => {
    if (!configured) { toast.error('SalesPRO: Primero guarda las credenciales'); return; }
    setTesting(true);
    try {
      const r = await api.testGlobalSmtp(testEmail || undefined);
      toast.success(`SalesPRO: Correo enviado a ${r.data.sent_to}. Revisa tu bandeja (y spam).`);
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Fallo el envío'); }
    finally { setTesting(false); }
  };
  return (
    <Card data-testid="global-gmail-card"><CardContent className="p-5 space-y-3">
      <h3 className="font-heading font-bold text-[#022C22] flex items-center gap-2"><Mail size={18} /> Gmail del sistema {configured ? <span className="text-xs text-green-600">(configurado)</span> : <span className="text-xs text-amber-600">(pendiente)</span>}</h3>
      <p className="text-xs text-[#64748B]">Se usa como <b>fallback</b> para enviar correos (afiliaciones cross-tenant, reset de contraseñas, estados de cuenta cuando una empresa no tiene su propio SMTP). Cada empresa afiliada puede tener su propio correo en /mall-config, y si no lo configura se usa éste. Requiere contraseña de aplicación de Google.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Correo Gmail</Label><Input data-testid="gmail-user" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="tucorreo@gmail.com" /></div>
        <div><Label>Contraseña de aplicación</Label><Input data-testid="gmail-pass" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder={configured ? '•••• (sin cambios)' : ''} /></div>
        <div><Label>Nombre remitente</Label><Input value={f.from_name} onChange={(e) => setF({ ...f, from_name: e.target.value })} /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} data-testid="gmail-save"><Save size={14} className="mr-1" /> Guardar Gmail</Button>
      </div>
      <div className="rounded-md border border-primary/30 bg-[#F0FBF7] p-3 space-y-2">
        <p className="text-xs font-semibold text-[#022C22]">🧪 Probar envío (comprueba que REALMENTE funciona)</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Correo destino (vacío = tu correo)</Label>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="prueba@gmail.com" data-testid="smtp-test-to" />
          </div>
          <Button onClick={sendTest} disabled={testing || !configured} variant="outline" data-testid="smtp-test-send">
            {testing ? 'Enviando…' : 'Enviar correo de prueba'}
          </Button>
        </div>
        <p className="text-[11px] text-[#64748B]">Si el correo llega a tu bandeja (o a spam), la configuración está correcta y funcional. Si falla, revisa el registro de errores.</p>
      </div>
    </CardContent></Card>
  );
};

export default function License() {
  const [lic, setLic] = useState(null);
  const [pay, setPay] = useState({ amount: '', reference: '', months: 1, note: '' });
  const [newMod, setNewMod] = useState({ key: '', label: '' });
  const { reload } = useLicense();
  const load = async () => { try { setLic((await api.getLicense()).data); } catch { toast.error('SalesPRO: Solo el Super Master'); } };
  useEffect(() => { load(); }, []);
  if (!lic) return <div className="p-6 text-[#64748B]">Cargando…</div>;

  const toggleRole = (mod, role) => {
    const cur = lic.matrix[mod] || [];
    setLic({ ...lic, matrix: { ...lic.matrix, [mod]: cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role] } });
  };
  const toggleMinimal = (mod) => setLic({ ...lic, minimal_modules: lic.minimal_modules.includes(mod) ? lic.minimal_modules.filter((m) => m !== mod) : [...lic.minimal_modules, mod] });
  const save = async () => {
    try {
      const { plan_name, monthly_fee, renew_date, grace_days, auto_suspend, matrix, minimal_modules, catalog } = lic;
      setLic((await api.updateLicense({ plan_name, monthly_fee: parseFloat(monthly_fee) || 0, renew_date, grace_days: parseInt(grace_days) || 0, auto_suspend, matrix, minimal_modules, catalog })).data);
      reload(); toast.success('SalesPRO: Configuración de funciones guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al guardar'); }
  };
  const action = async (fn, body, msg) => { try { setLic((await fn(body)).data); reload(); toast.success(msg); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } };
  const addModule = () => {
    if (!newMod.key.trim()) return;
    const key = newMod.key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setLic({ ...lic, catalog: [...lic.catalog, { key, label: newMod.label || key }], matrix: { ...lic.matrix, [key]: [] } });
    setNewMod({ key: '', label: '' });
  };
  const suspended = lic.status === 'suspended';

  return (
    <div className="space-y-5" data-testid="license-page">
      {/* Header compacto */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black text-[#022C22] flex items-center gap-2"><ShieldCheck /> Administración SalesPRO</h1>
          <p className="text-sm text-[#64748B]">Panel exclusivo del Super Master: licencia, facturación, colaboradores y errores.</p>
        </div>
        <span data-testid="license-status" className={`px-4 py-2 rounded-full text-sm font-bold ${suspended ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {suspended ? 'SUSPENDIDA — funciones mínimas' : 'ACTIVA'} · vence {fmtDate(lic.renew_date)} ({lic.days_left} días)
        </span>
      </div>

      {/* Banner rojo SOLO si realmente está suspendida */}
      {suspended && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2 text-sm" data-testid="license-suspended-banner">
          <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
          <p className="text-red-700">Licencia SalesPRO <b>suspendida</b> por falta de pago. Solo las funciones mínimas están activas hasta que confirmes el pago en la pestaña "Pago / Membresía".</p>
        </div>
      )}

      <Tabs defaultValue="payment" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6 h-auto">
          <TabsTrigger value="payment" className="flex items-center gap-1" data-testid="tab-payment"><CreditCard size={14} /> Pago / Membresía</TabsTrigger>
          <TabsTrigger value="functions" className="flex items-center gap-1" data-testid="tab-functions"><SlidersHorizontal size={14} /> Funciones</TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-1" data-testid="tab-billing"><Receipt size={14} /> Facturación SaaS</TabsTrigger>
          <TabsTrigger value="marketplace" className="flex items-center gap-1" data-testid="tab-marketplace"><Megaphone size={14} /> Marketplace</TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-1" data-testid="tab-team"><UsersIcon size={14} /> Mano derecha</TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-1" data-testid="tab-system"><Mail size={14} /> Sistema</TabsTrigger>
        </TabsList>

        {/* ========================= PESTAÑA PAGO ========================= */}
        <TabsContent value="payment" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card><CardContent className="p-5 space-y-3">
              <h3 className="font-heading font-bold text-[#022C22]">Licencia global de SalesPRO (tu instancia)</h3>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Esto controla la licencia y los módulos de TODA tu instancia SalesPRO (uso interno del Super Master). <b>No es el plan de una empresa cliente.</b> Para fijar precio y fecha de corte por empresa, ve a <b>Facturación SaaS → Planes por empresa</b>.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre del plan</Label><Input value={lic.plan_name} onChange={(e) => setLic({ ...lic, plan_name: e.target.value })} data-testid="license-plan-name" /></div>
                <div><Label>Mensualidad (USD)</Label><Input type="number" value={lic.monthly_fee} onChange={(e) => setLic({ ...lic, monthly_fee: e.target.value })} /></div>
                <div><Label>Fecha de renovación</Label><Input type="date" value={lic.renew_date} onChange={(e) => setLic({ ...lic, renew_date: e.target.value })} data-testid="license-renew-date" /></div>
                <div><Label>Días de gracia</Label><Input type="number" value={lic.grace_days} onChange={(e) => setLic({ ...lic, grace_days: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!lic.auto_suspend} onCheckedChange={(v) => setLic({ ...lic, auto_suspend: v })} /> Suspender automáticamente al vencer</label>
              <Button variant="outline" onClick={save} data-testid="license-save-plan"><Save size={14} className="mr-1" /> Guardar plan</Button>
            </CardContent></Card>

            <Card><CardContent className="p-5 space-y-3">
              <h3 className="font-heading font-bold text-[#022C22]">Confirmar pago</h3>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Monto</Label><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="license-pay-amount" /></div>
                <div><Label>Referencia</Label><Input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
                <div><Label>Meses</Label><Input type="number" min="1" value={pay.months} onChange={(e) => setPay({ ...pay, months: e.target.value })} /></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="license-confirm-payment" onClick={() => action(api.confirmLicensePayment, { ...pay, amount: parseFloat(pay.amount) || 0, months: parseInt(pay.months) || 1 }, 'SalesPRO: Pago confirmado, funciones restablecidas')}><Unlock size={14} className="mr-1" /> Confirmar pago y desbloquear</Button>
                <Button variant="outline" className="text-red-600 border-red-200" data-testid="license-suspend" onClick={() => action(api.suspendLicense, { note: 'Falta de pago' }, 'SalesPRO: Licencia suspendida')}><Lock size={14} className="mr-1" /> Suspender ahora</Button>
              </div>
              <details>
                <summary className="text-xs text-[#334155] cursor-pointer">Historial reciente</summary>
                <div className="max-h-32 overflow-y-auto text-xs text-[#64748B] space-y-1 mt-2">
                  {[...(lic.history || [])].reverse().map((h, i) => <div key={i}>{new Date(h.at).toLocaleString()} · <b>{h.action}</b> · {h.by} {h.amount ? `· $${h.amount}` : ''} {h.note || ''}</div>)}
                </div>
              </details>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* ========================= PESTAÑA FUNCIONES ========================= */}
        <TabsContent value="functions" className="space-y-4">
          <Card><CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <h3 className="font-heading font-bold text-[#022C22]">Funciones por nivel jerárquico</h3>
                <p className="text-xs text-[#64748B]">Define qué función ve cada rol. "Mínima" = lo único activo si la licencia se suspende.</p>
              </div>
              <Button onClick={save} data-testid="license-save"><Save size={14} className="mr-1" /> Guardar cambios</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="license-matrix">
                <thead><tr className="text-left text-xs text-[#64748B] border-b sticky top-0 bg-white">
                  <th className="py-2 pr-3">Función</th>{lic.roles.map((r) => <th key={r} className="px-2 text-center">{ROLE_LABEL[r] || r}</th>)}
                  <th className="px-2 text-center text-red-600">Mínima</th>
                </tr></thead>
                <tbody>{lic.catalog.map((m) => (
                  <tr key={m.key} className="border-b border-[#F1F5F9]">
                    <td className="py-2 pr-3 font-medium text-[#022C22]">{m.label}<span className="block text-[10px] text-[#94A3B8] font-mono">{m.key}</span></td>
                    {lic.roles.map((r) => <td key={r} className="text-center"><Switch data-testid={`matrix-${m.key}-${r}`} checked={(lic.matrix[m.key] || []).includes(r)} onCheckedChange={() => toggleRole(m.key, r)} /></td>)}
                    <td className="text-center"><Switch data-testid={`minimal-${m.key}`} checked={lic.minimal_modules.includes(m.key)} onCheckedChange={() => toggleMinimal(m.key)} /></td>
                  </tr>))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-end gap-2 mt-4 pt-4 border-t">
              <div><Label className="text-xs">Nueva función (clave)</Label><Input value={newMod.key} onChange={(e) => setNewMod({ ...newMod, key: e.target.value })} placeholder="ej. garantias" data-testid="new-module-key" /></div>
              <div><Label className="text-xs">Nombre visible</Label><Input value={newMod.label} onChange={(e) => setNewMod({ ...newMod, label: e.target.value })} placeholder="Garantías" /></div>
              <Button variant="outline" onClick={addModule} data-testid="add-module-btn"><Plus size={14} className="mr-1" /> Agregar función futura</Button>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ========================= PESTAÑA FACTURACIÓN SAAS ========================= */}
        <TabsContent value="billing" className="space-y-4">
          <TenantPlans />
          <VendorBilling />
        </TabsContent>

        {/* ========================= PESTAÑA MARKETPLACE ========================= */}
        <TabsContent value="marketplace" className="space-y-4">
          <MarketplaceAdmin />
        </TabsContent>

        {/* ========================= PESTAÑA MANO DERECHA ========================= */}
        <TabsContent value="team" className="space-y-4">
          <RightHandManager />
        </TabsContent>

        {/* ========================= PESTAÑA SISTEMA ========================= */}
        <TabsContent value="system" className="space-y-4">
          <GmailCard />
          <SystemOps />
          <ErrorLogs />
        </TabsContent>
      </Tabs>
    </div>
  );
}
